
import React from "react";
import SegmentSection from "./SegmentSection";
import { formatCurrency, formatDate } from "@/lib/formatters";

interface GroupedProduct {
  id: number;
  nome: string;
  marca: string;
  precoAluno2025: number;
  valorUnitario: number;
  qtdReferencia: number;
  quantidade: number;
  valorTotal: number;
}

interface BrandGroup {
  nome: string;
  brandId: number;
  produtos: GroupedProduct[];
  total: number;
  somaPrecoAluno2025: number;
  somaPrecoAluno2026: number;
}

interface SegmentGroup {
  nome: string;
  brands: BrandGroup[];
  total: number;
}

interface PDFSegmentPageProps {
  segment: SegmentGroup;
  segmentIndex: number;
  totalSegments: number;
  orderNumber: string;
  clientName: string;
  grandTotal: number;
}

const PDFSegmentPage = ({
  segment,
  segmentIndex,
  totalSegments,
  orderNumber,
  clientName,
  grandTotal,
}: PDFSegmentPageProps) => {
  const getSafeDate = () => {
    const d = new Date();
    if (isNaN(d.getTime()) || d.getFullYear() < 2020) {
      return new Date(Date.now());
    }
    return d;
  };

  const safeNow = getSafeDate();
  const day = String(safeNow.getDate()).padStart(2, '0');
  const month = String(safeNow.getMonth() + 1).padStart(2, '0');
  const year = safeNow.getFullYear();

  const formattedDate = `${day}/${month}/${year}`;

  return (
    <div 
      className="bg-white page-break-before" 
      style={{ 
        width: '210mm', 
        height: '297mm',
        padding: '15mm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column'
      }}
      data-segment={`segment-${segmentIndex}`}
    >
      {/* Page Header */}
      <div className="text-center mb-6 border-b-2 border-gray-300 pb-4 flex-shrink-0">
        <h1 className="text-xl font-bold text-gray-800 mb-1">
          EXTRATO DETALHADO DO PEDIDO
        </h1>
        <div className="text-lg font-semibold text-blue-600 mb-1">#{orderNumber}</div>
        <div className="text-gray-700 mb-1">{clientName}</div>
        <div className="text-sm text-gray-600">
          Gerado em: {formattedDate} • Página {segmentIndex + 2} de {totalSegments + 1}
        </div>
      </div>

      {/* Segment Content - Takes available space */}
      <div className="flex-1 overflow-hidden">
        <SegmentSection
          nome={segment.nome}
          brands={segment.brands}
          total={segment.total}
          isCompactMode={true}
        />
      </div>

      {/* Page Footer - only on last segment */}
      {segmentIndex === totalSegments - 1 && (
        <div className="mt-auto pt-4 border-t-2 border-gray-300 flex-shrink-0">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">
                  Total Geral do Extrato
                </h3>
                <div className="text-xs text-gray-600">
                  {totalSegments} {totalSegments === 1 ? 'segmento' : 'segmentos'} • {' '}
                  {segment.brands.length} {segment.brands.length === 1 ? 'marca' : 'marcas'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(grandTotal)}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-3 text-center text-xs text-gray-600">
            <div>Este extrato detalhado complementa o resumo executivo do pedido.</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFSegmentPage;
